import React, { useState } from 'react';
import { Upload, Trash2, CheckCircle } from 'lucide-react';

const ALLOWED_EXTS = ['pdf', 'docx', 'doc', 'xlsx'];

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop();
  if (ext === 'pdf') return '\u{1F4C4}';
  if (ext === 'doc' || ext === 'docx') return '\u{1F4DD}';
  if (ext === 'xls' || ext === 'xlsx') return '\u{1F4CA}';
  return '\u{1F4CE}';
}

interface Props {
  file: File | null;
  onFileChange: (file: File | null) => void;
  onError: (msg: string) => void;
  disabled?: boolean;
}

export default function FileDrop({ file, onFileChange, onError, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f: File | null) => {
    if (f) {
      const ext = f.name.toLowerCase().split('.').pop() || '';
      if (!ALLOWED_EXTS.includes(ext)) {
        onError(`Неподдерживаемый формат файла: ${f.name}. Поддерживаются: docx, doc, pdf, xlsx`);
        return;
      }
    }
    onFileChange(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleFile(files[0]);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        } ${
          dragOver
            ? 'border-pink-500 bg-pink-100 scale-[1.01]'
            : file
              ? 'border-green-400 bg-green-50'
              : 'border-gray-300 hover:border-pink-400 hover:bg-pink-50'
        }`}
      >
        <label className={`block ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
          <input
            type="file"
            className="hidden"
            accept=".docx,.doc,.pdf,.xlsx"
            disabled={disabled}
            onChange={(e) => handleFile(e.target.files?.[0] || null)}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <span className="text-3xl">{getFileIcon(file.name)}</span>
              <div className="text-left min-w-0">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                </div>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              </div>
            </div>
          ) : dragOver ? (
            <div className="text-pink-600 py-4">
              <Upload className="w-8 h-8 mx-auto mb-2 animate-bounce" />
              <span className="font-medium">Отпустите файл здесь</span>
            </div>
          ) : (
            <div className="text-gray-500 py-2">
              <Upload className="w-8 h-8 mx-auto mb-2" />
              <span className="text-sm font-medium">Перетащите файл сюда</span>
              <p className="text-xs text-gray-400 mt-1">или нажмите для выбора</p>
              <p className="text-xs text-pink-400 mt-2">docx, doc, pdf, xlsx</p>
            </div>
          )}
        </label>
      </div>
      {file && !disabled && (
        <button
          onClick={() => onFileChange(null)}
          className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1 mx-auto"
        >
          <Trash2 className="w-3 h-3" /> Удалить файл
        </button>
      )}
    </div>
  );
}
